'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import UICardGrid from 'UIComponents/card/UICardGrid';
import UILink from 'UIComponents/link/UILink';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import { FEATURED_CALLOUT_LAYOUT_TYPES } from 'ecosystem-marketplace-core/constants/FeaturedCallouts';
import ResultsSection from 'ecosystem-marketplace-core/components/storefront/ResultsSection';
import { useResponsiveProps } from 'ecosystem-marketplace-core/hooks/useResponsiveProps';
import CoreFeaturedCallout from 'ecosystem-marketplace-core/components/callouts/FeaturedCallout';
import ValidI18nMessage from 'ecosystem-marketplace-core/i18n/components/ValidI18nMessage';
import { isFulfilled, isUninitialized } from 'ecosystem-marketplace-core/utils/requestState';
import { getFeaturedCallouts } from '../../actions/FeaturedCalloutsActions';
import { makeGetCollectionListingById } from '../../selectors/collections';
import { getDisplayedFeaturedCalloutListings, getFeaturedCalloutsRequestState } from '../../selectors/featuredCallouts';
import StorefrontSectionMarker from './StorefrontSectionMarker';

const FeaturedCallout = ({
  layout,
  listing,
  onClick
}) => {
  const {
    calloutId,
    headline,
    description,
    illustration,
    gradientColor,
    buttonText,
    collectionId,
    externalUrl
  } = listing;
  const getCollectionListingById = useMemo(makeGetCollectionListingById, []);
  const collectionListing = useSelector(state => collectionId ? getCollectionListingById(state, {
    collectionId
  }) : undefined);
  const i18nKey = typeof calloutId === 'string' ? calloutId : `featuredCallout_${calloutId}`;
  let Link;
  const linkProps = {};

  if (externalUrl) {
    Link = UILink;
    linkProps.href = externalUrl;
  } else if (collectionListing) {
    Link = UIRouterLink;
    linkProps.to = `/${collectionListing.slug}`;
  }

  if (!Link) {
    return null;
  }

  return /*#__PURE__*/_jsx(CoreFeaturedCallout, {
    buttonText: /*#__PURE__*/_jsx(ValidI18nMessage, {
      message: `appMarketplace.featuredCallouts.${i18nKey}.buttonText`,
      defaultValue: buttonText
    }),
    description: /*#__PURE__*/_jsx(ValidI18nMessage, {
      message: `appMarketplace.featuredCallouts.${i18nKey}.description`,
      defaultValue: description
    }),
    external: !!externalUrl,
    gradientColor: gradientColor,
    headline: /*#__PURE__*/_jsx(ValidI18nMessage, {
      message: `appMarketplace.featuredCallouts.${i18nKey}.headline`,
      defaultValue: headline
    }),
    illustrationName: illustration,
    layout: layout,
    Link: Link,
    linkProps: linkProps,
    onClick: onClick
  });
};

const FeaturedCalloutsSection = ({
  onFeaturedCalloutsMount,
  sectionIndex,
  trackSectionInteraction
}) => {
  const dispatch = useDispatch();
  const listings = useSelector(getDisplayedFeaturedCalloutListings);
  const totalListings = listings.size;
  const requestState = useSelector(getFeaturedCalloutsRequestState);
  const {
    columns
  } = useResponsiveProps({
    columns: {
      xs: 1,
      md: totalListings > 1 ? 2 : 1
    }
  });
  const layout = columns > 1 ? FEATURED_CALLOUT_LAYOUT_TYPES.MULTI : FEATURED_CALLOUT_LAYOUT_TYPES.FULL_WIDTH;
  const renderedFeaturedCallouts = useMemo(() => listings.map((listing, listingIndex) => {
    const {
      id: listingId,
      calloutId
    } = listing;
    const sectionId = `${calloutId}${listingId ? `_${listingId}` : ''}`;
    const sectionName = `featured-callout::${sectionId}::static`;
    const sectionPlacementValue = sectionIndex + listingIndex / totalListings;
    return /*#__PURE__*/_jsx(StorefrontSectionMarker, {
      name: sectionName,
      placementValue: sectionPlacementValue,
      children: /*#__PURE__*/_jsx(FeaturedCallout, {
        layout: layout,
        listing: listing,
        onClick: () => trackSectionInteraction(sectionName, 'click amplify callout')
      })
    }, sectionName);
  }).toArray(), [layout, listings, sectionIndex, totalListings, trackSectionInteraction]);
  useEffect(() => {
    if (isUninitialized(requestState)) {
      dispatch(getFeaturedCallouts());
    }
  }, [dispatch, requestState]);
  useEffect(() => {
    if (isFulfilled(requestState)) {
      onFeaturedCalloutsMount();
    }

    return () => {};
  }, [onFeaturedCalloutsMount, requestState]);

  if (!totalListings) {
    return null;
  }

  return /*#__PURE__*/_jsx(ResultsSection, {
    children: /*#__PURE__*/_jsx(UICardGrid, {
      columns: columns,
      children: renderedFeaturedCallouts
    })
  });
};

FeaturedCalloutsSection.propTypes = {
  onFeaturedCalloutsMount: PropTypes.func.isRequired,
  sectionIndex: PropTypes.number.isRequired,
  trackSectionInteraction: PropTypes.func.isRequired
};
export default FeaturedCalloutsSection;